<template>
  <div class="w-full p-6">
    <div
      class="w-full p-6 bg-white border rounded-lg border-mid-neutral-50 grid grid-cols-[1fr_3fr] gap-8"
    >
      <div class="space-y-1">
        <h3 class="font-semibold text-dark-neutral-500">
          Assign evaluators and roles
        </h3>
        <p class="text-sm text-gray-600 font-light">
          Manage who participates in this evaluation and what they can do.
        </p>
      </div>
      <div class="w-full">
        <div class="w-full border rounded-lg border-mid-neutral-50">
          <div class="w-full text-sm py-7.5">
            <div class="pl-14 pr-5">
              <div
                class="pl-3 grid grid-cols-3 font-medium text-mid-neutral-300"
              >
                <span>Role</span>
                <span>Name</span>
                <span></span>
              </div>

              <hr class="w-full border-mid-neutral-50 mt-2.5" />
            </div>
            <Sortable
              :key="
                review.entity.value.evaluators
                  .map((e) => e.id + e.order)
                  .join('-')
              "
              :items="review.entity.value.evaluators"
              @item:moved="
                ({ fractionalIndex, item }) =>
                  moveEvaluator(item, fractionalIndex)
              "
            >
              <template #item="{ item, onDragEnd, onDragStart }">
                <div
                  class="flex w-full items-center group px-3 hover:bg-light-neutral-300"
                >
                  <div
                    :class="{
                      'invisible group-hover:visible':
                        !review.isPlanReadonly.value,
                      invisible: review.isPlanReadonly.value,
                    }"
                  >
                    <PlanLockGuard>
                      <button
                        class="py-1 text-secondary hover:brightness-125 text-mid-neutral-500"
                        :disabled="review.isPlanReadonly.value"
                        @click.stop="createEvaluator"
                      >
                        <PlusIcon class="size-5" />
                      </button>
                    </PlanLockGuard>
                  </div>

                  <SortableItem
                    class="w-full"
                    :drag-handle-classes="
                      review.isPlanReadonly.value
                        ? 'invisible'
                        : 'invisible group-hover:visible'
                    "
                    @dragstart="onDragStart"
                    @dragend="onDragEnd"
                  >
                    <EvaluatorComponent :evaluator="item" />
                  </SortableItem>
                </div>
              </template>
            </Sortable>
            <div
              v-if="review.entity.value.evaluators.length === 0"
              class="pl-14"
            >
              <PlanLockGuard>
                <button
                  class="w-full max-w-sm text-sm font-medium flex items-center text-secondary-500 py-3 gap-1"
                  :disabled="review.isPlanReadonly.value"
                  @click.stop="createEvaluator"
                >
                  <PlusIcon class="size-4" />
                  Add evaluator
                </button>
              </PlanLockGuard>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import EvaluatorComponent from './Evaluator.vue'
import { PlusIcon } from '@heroicons/vue/24/outline'
import Sortable from '@app/components/Global/Sortable/Sortable.vue'
import SortableItem from '@app/components/Global/Sortable/SortableItem.vue'
import { Evaluator } from '@core/domain/models/evaluator.model'
import { generateKeyBetween } from 'fractional-indexing'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import PlanLockGuard from '../PlanLockGuard.vue'

const review = injectStrict(ReviewKey)
const loading = useLoading()
const snackbar = useSnackbar()

async function createEvaluator() {
  loading.start()

  let order = 'a0'
  const lastEvaluator = review.entity.value.evaluators.at(-1)
  if (lastEvaluator) {
    order = generateKeyBetween(lastEvaluator.order, null)
  }

  try {
    await review.createEvaluator({
      name: '',
      role: '',
      order,
    })
    snackbar.success('Success')
  } catch {
    snackbar.error('Failed to create evaluator')
  } finally {
    loading.stop()
  }
}

async function moveEvaluator(evaluator: Evaluator, order: string) {
  loading.start()
  try {
    await review.updateEvaluator({
      ...evaluator,
      order,
    })
  } finally {
    loading.stop()
  }
}
</script>
